import {
  AUDIENCE_COMPLIANCE_STATUS,
  AUDIENCE_EXPORT_TYPES,
  ENUM_SYSTEM_USED,
  EXPORT_TYPES_FIELDS_REQUIRED,
  LoadingStates,
  MODEL_SCORING_TYPE,
  type ModelScoringTypeValue,
  PLATFORMS_KEYS,
  REQUIRED_FIELDS_ARE_MISSING,
  ValidationErrorMessages,
} from '../../constants';
import { Alert, Button, Grid, theme } from '@attain-sre/attain-design-system';
import {
  ToggleExportDialogAction,
  addNotification,
  fetchCombinedAudience,
  toggleExportDialog,
} from '../../pages/audienceManager/slice';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useUser } from 'reactfire';
// Material UI Components
import AudienceExport from '../AudienceExport/InhouseAudienceExport';
import ComplianceWarningModal from '../ComplianceWarningModal';
import DialogContent from '@material-ui/core/DialogContent';
import InlineExportHistory from '../../pages/audienceManager/export/InlineExportHistory';
import RenderEstimatedAudienceSize from './RenderEstimatedAudienceSize.jsx';
import Typography from '@material-ui/core/Typography';
import axios from 'axios';
import tokens from 'tokens';
import {
  ACTIVE_AUDIENCES,
  AUDIENCE_EXPORT_FAILED_MESSAGE,
  AUDIENCE_EXPORT_SUCCESS_MESSAGE,
  COMPLIANCE_WARNING_BLOCKED_NO_BAN_ROWS,
  COMPLIANCE_WARNING_POLICY_FULL_BAN,
  COMPLIANCE_WARNING_POLICY_PARTIAL_BAN,
} from '../../content';
import {
  API_BASE_ADDRESS,
  AUDIENCE_STUDIO_API_ENDPOINT,
} from '../../pages/audienceManager/variables';
import { Box, CircularProgress, Divider, Tooltip } from '@mui/material';
import { Info } from '@phosphor-icons/react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { getAudienceExportFields } from '../../pages/audienceManager/export/audienceExportSegmentConfig';
import { isCPMValid, isFloat, isSegmentNameValid } from '../../helper/helper';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(3),
    },
    divider: {
      borderLeft: '0.2em solid ' + tokens.colorDividerLgBorder,
    },
    email: {
      marginTop: '24px',
      marginBottom: theme.spacing(3),
      marginLeft: '6px',
    },
    title: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontWeight: 600,
      fontSize: 22,
      lineHeight: '32px',
      minWidth: 1000,
    },
    exportTitle: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontWeight: 600,
      fontSize: 22,
      lineHeight: '32px',
      minWidth: 1000,
      margin: '1rem 0 0 1.5rem',
    },
    selectExport: {
      width: '100%',
    },
    controlLabel: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontSize: 16,
      fontWeight: 500,
      paddingBottom: 4,
    },
    emailLabel: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontSize: 16,
      fontWeight: 500,
      paddingBottom: 10,
      paddingTop: 40,
    },
    exportSeedAudienceContainer: {
      paddingTop: 20,
      backgroundColor: 'rgb(240, 239, 241)',
      padding: 15,
      marginTop: 36,
      borderRadius: 8,
      width: '100%',
    },
    divider: {
      border: '1px solid #e4e4e4',
      marginTop: 20,
      width: '100%',
    },
    exportExportButton: {
      textAlign: 'right',
      marginTop: 15,
    },
    audienceDefinitionBox: {
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #ccc',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      flex: 1,
      background: 'white',
    },
    header: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontWeight: 600,
      fontSize: 22,
      lineHeight: '32px',
    },
    body: {
      fontFamily: 'Calibre-Regular,sans-serif',
      fontSize: 16,
      marginTop: '4px',
    },
    modelScoringLabel: {
      display: 'block',
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontSize: 16,
      marginBottom: 8,
    },
  })
);

interface Props {
  lalModelType: string;
  lastCompletedExportSize?: number;
  audienceName: string;
  audienceType: string;
  isSelectAudienceVisible: boolean;
  modeledAudiences: any;
}

const InlineAudienceExport = ({
  lalModelType = 'transunion',
  lastCompletedExportSize,
  audienceName,
  audienceType,
  isSelectAudienceVisible,
  modeledAudiences,
}: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { uiAudienceManagerAudienceComplianceModuleEnabled = false } =
    useFlags();

  // Retrieve the current audience details
  const audience = useAppSelector(state => {
    if (state.audienceReducer && state.audienceReducer.currentAudience) {
      return state.audienceReducer.currentAudience;
    } else {
      return null;
    }
  });

  const { data: user } = useUser();
  const currentUser = user || { email: '' };
  const [emails, setEmails] = useState(currentUser.email);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedExportTypes, setSelectedExportTypes] = useState([]);
  const [selectedSeedAudienceExportTypes, setSelectedSeedAudienceExportTypes] =
    useState([]);
  const [exportDefaultFields, setExportDefaultFields] = useState({
    segmentName: audienceName,
    description: '',
    cpm: '',
    percentageOfMedia: '',
  });
  const [exportDynamicFields, setExportDynamicFields] = useState([]);
  const [exportDynamicValues, setExportDynamicValues] = useState([]);
  const [complianceWarningOpen, setComplianceWarningOpen] = useState(false);
  const [complianceWarningTableData, setComplianceWarningTableData] = useState<
    Array<{
      category: string;
      policy_label: string;
      matched_keywords: string[];
      matched_brands: string[];
    }>
  >([]);

  const { segmentName, description, cpm, percentageOfMedia } =
    exportDefaultFields;

  useEffect(() => {
    // Prepare UI fields based on the selected export types
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    const preparedUIFields = getAudienceExportFields(allExports);
    setExportDynamicFields(preparedUIFields);

    // Prepare form data for each UI field
    const formDataFields = initializeFormDataFields(preparedUIFields);
    setExportDynamicValues(formDataFields);
  }, [selectedExportTypes, selectedSeedAudienceExportTypes]);

  /* Handler functions | Start */

  const dispatchClose = () => {
    const actionProps: ToggleExportDialogAction = {
      open: false,
    };
    dispatch(toggleExportDialog(actionProps));
  };

  const handleSelectedExportType = (exportTypes: Array<string>) => {
    setSelectedExportTypes(exportTypes);
  };

  const handleChangeField = (id: string, value: any, segmentId: string) => {
    if (segmentId) {
      // If segmentId exists, update the export dynamic fields
      const updatedForm = updateFormData(
        exportDynamicValues,
        id,
        value,
        segmentId
      );
      setExportDynamicValues(updatedForm);
    } else {
      setExportDefaultFields({ ...exportDefaultFields, [id]: value });
    }
  };

  const updateFormData = (
    formData: any[],
    id: string,
    value: any,
    segmentId: string
  ) => {
    // Find the existing form data by segmentId
    const existingFormData = formData.find(x => x.id === segmentId);
    if (existingFormData) {
      existingFormData[id] = value; // Update the field value

      switch (id) {
        case 'platformDestination':
          existingFormData.advertiserDestination = '';
          break;
        case 'advertiserDestination':
          if (!value) existingFormData.platformDestination = '';
          break;
        default:
          break;
      }

      return [...formData]; // Return a new array with the updated form data
    }

    return formData;
  };

  const startExport = () => {
    if (audience) {
      const allExports = [
        ...selectedExportTypes,
        ...selectedSeedAudienceExportTypes,
      ];


      const cpmValidFor = [
        ...EXPORT_TYPES_FIELDS_REQUIRED.cpm,
        ...EXPORT_TYPES_FIELDS_REQUIRED.default,
      ];
      const pomValidFor = [
        ...EXPORT_TYPES_FIELDS_REQUIRED.percentageOfMedia,
        ...EXPORT_TYPES_FIELDS_REQUIRED.default,
      ];

      const isCPM = cpmValidFor.some((x) => allExports.includes(x));
      const isPOM = pomValidFor.some((x) => allExports.includes(x));

      if (isCPM && cpm !== '' && !isFloat(cpm)) {
        dispatch(
          addNotification({
            state: LoadingStates.ERROR,
            message: 'Please enter valid CPM',
          })
        );
        return;
      }
      if (isPOM && percentageOfMedia !== '' && !isFloat(percentageOfMedia)) {
        dispatch(
          addNotification({
            state: LoadingStates.ERROR,
            message: 'Please enter valid % of media',
          })
        );
        return;
      }
      handleExportToTransunion(false);
    }
  };

  const handleComplianceWarningClose = () => {
    setComplianceWarningOpen(false);
    setComplianceWarningTableData([]);
  };

  const handleComplianceWarningCancel = () => {
    setComplianceWarningOpen(false);
    setComplianceWarningTableData([]);
  };

  const handleComplianceWarningConfirm = () => {
    setComplianceWarningOpen(false);
    setComplianceWarningTableData([]);
    handleExportToTransunion(true);
  };

  /* Handler functions | End */

  /* Util functions | Start */

  const initializeFormDataFields = (fields: any[]) => {
    return fields.map(field => {
      // Get column names from the field form JSON
      const columnNames =
        field.formJSON
          ?.filter(column => column.column)
          .map(column => column.column) || [];

      // Initialize form data with the field id and export type
      const formData = { id: field.id, exportType: field.exportType };

      // Add each column as a field in the form data
      if (columnNames.length > 0) {
        columnNames.forEach(column => {
          formData[column] = ''; // Initialize column value as empty
        });
      }

      return formData;
    });
  };

  const clearData = (isSuccess: boolean) => {
    setIsExporting(false);
    dispatchClose();
    if (isSuccess) {
      setExportDynamicFields([]);
      setSelectedExportTypes([]);
      setSelectedSeedAudienceExportTypes([]);
      setExportDynamicValues([]);
      setExportDefaultFields({
        segmentName: audienceName,
        description: '',
        cpm: '',
        percentageOfMedia: '',
      });
    }
  };

  const prepareExportPayload = () => {
    const resultPayload = [];
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    for (const exportType of allExports) {
      const isSeedAudienceExport =
        selectedSeedAudienceExportTypes.includes(exportType);

      const payload: Record<string, unknown> = {
        id: audience.id,
        emails: currentUser.email,
        segmentName,
        description: description?.trim(),
        cpm: cpm !== '' ? parseFloat(cpm) : '',
        percentageOfMedia:
          percentageOfMedia !== '' ? parseFloat(percentageOfMedia) : '',
        lalModelType: lalModelType,
        combinedAudienceUsersCount: audience?.totalSize,
        combinedAudienceGeneratorRequestId: audience?.generationId,
        includedAudiences: audience?.audiences,
      };

      if (!isSeedAudienceExport) {
        payload.modelScoringType = MODEL_SCORING_TYPE.LAL;
      }
      const currentFormData = exportDynamicValues.find(
        x => x.exportType === exportType
      );
      if (currentFormData) {
        payload['partnerID'] = currentFormData?.partnerID || '';
        payload['advertiserID'] = currentFormData?.advertiserID || '';
        payload['tvCpm'] = currentFormData?.tvTargeting
          ? parseFloat(currentFormData?.tvTargeting)
          : '';
      }

      switch (exportType) {
        case AUDIENCE_EXPORT_TYPES.OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE:
        case AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const otsPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'trade_desk',
          };
          resultPayload.push(otsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT: {
          const customPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'liveramp',
            platformDestination: currentFormData?.platformDestination?.id || '',
            advertiserDestination:
              currentFormData?.advertiserDestination?.id || '',
            advertiserDirectPercentageOfMedia:
              currentFormData?.advertiserDirect !== ''
                ? parseInt(currentFormData?.advertiserDirect)
                : null,
          };
          resultPayload.push(customPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE: {
          const customPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'liveramp',
            platformDestination: '',
            advertiserDestination: '',
            advertiserDirectPercentageOfMedia:
              currentFormData?.advertiserDirect !== ''
                ? parseInt(currentFormData?.advertiserDirect)
                : null,
          };
          resultPayload.push(customPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const customPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'liveramp',
            platformDestination: currentFormData?.platformDestination?.id || '',
            advertiserDestination:
              currentFormData?.advertiserDestination?.id || '',
            advertiserDirectPercentageOfMedia:
              currentFormData?.advertiserDirect !== ''
                ? parseInt(currentFormData?.advertiserDirect)
                : null,
          };
          resultPayload.push(customPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.TRANSUNION:
        case AUDIENCE_EXPORT_TYPES.TRANSUNION_TTD:
        case AUDIENCE_EXPORT_TYPES.TRANSUNION_HEMS: {
          const customPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'transunion',
          };
          resultPayload.push(customPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT: {
          const pbPayload = {
            ...payload,
            exportType,
            percentageOfMedia: '',
            platformIntegrated: 'pubmatic',
          };
          resultPayload.push(pbPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT: {
          const opxOtsPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'openx',
            isOts: true,
            category:
              typeof currentFormData?.category === 'string'
                ? currentFormData.category.trim()
                : '',
            subCategory:
              typeof currentFormData?.subCategory === 'string'
                ? currentFormData.subCategory.trim()
                : '',
            type:
              typeof currentFormData?.type === 'string'
                ? currentFormData.type.trim()
                : '',
          };
          resultPayload.push(opxOtsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT: {
          const opxCustomPayload = {
            ...payload,
            exportType,
            description: '',
            cpm: '',
            platformIntegrated: 'openx',
            percentageOfMedia: '',
            isOts: false,
          };
          resultPayload.push(opxCustomPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT: {
          const opxPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'experian',
          };
          resultPayload.push(opxPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.X_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.X_CUSTOM_SEGMENT: {
          const opxPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'x',
          };
          resultPayload.push(opxPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.META_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT: {
          const opxPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'meta',
          };
          resultPayload.push(opxPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const metaPayload = {
            id: audience.id,
            emails: currentUser.email,
            segmentName,
            description,
            lalModelType,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.META_KEY,
            metaAdAccountId: currentFormData?.metaAdAccountId || '',
            metaLalRatio: currentFormData?.metaLalRatio || '',
            audienceSize: audience?.totalSize || 0,
          };
          resultPayload.push(metaPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const tiktokPayload = {
            id: audience.id,
            emails: currentUser.email,
            segmentName,
            description,
            lalModelType,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.TIKTOK_KEY,
            advertiserID: currentFormData?.advertiserID?.trim() || '',
            mobileOs: currentFormData?.mobileOs !== undefined ? [Number(currentFormData.mobileOs)] : [],
            audienceModelingBreadth: currentFormData?.audienceModelingBreadth?.trim() || '',
            placements: Array.isArray(currentFormData?.placements) ? currentFormData.placements : [],
            audienceType: audienceType,
          };
          resultPayload.push(tiktokPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.MAGNITE_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.MAGNITE_CUSTOM_SEGMENT: {
          const opxPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'magnite',
          };
          resultPayload.push(opxPayload);
          break;
        }
        default:
          break;
      }
    }

    return resultPayload;
  };

  const handleExportToTransunion = (isAudienceComplianceSkipped: boolean) => {
    setIsExporting(true);
    const exportsList = prepareExportPayload();
    const requestBody = {
      isAudienceComplianceSkipped:
        uiAudienceManagerAudienceComplianceModuleEnabled
          ? isAudienceComplianceSkipped
          : true,
      exports: exportsList,
    };
    axios
      .post(`${AUDIENCE_STUDIO_API_ENDPOINT}/export/platform`, requestBody)
      .then(() => {
        clearData(true);
        dispatch(
          addNotification({
            state: 'done',
            message: AUDIENCE_EXPORT_SUCCESS_MESSAGE,
          })
        );

        setTimeout(() => {
          dispatch(fetchCombinedAudience({ id: audience.id }));
        }, 3000);
      })
      .catch((error) => {
        const resData = error.response?.data;
        if (
          error.response?.status === 400 &&
          resData?.complianceErrors != null
        ) {
          setIsExporting(false);
          const violations = resData.complianceErrors?.global?.violations ?? [];
          const bannedOnly = violations.filter(
            (v: { policy?: string }) =>
              v.policy === AUDIENCE_COMPLIANCE_STATUS.FULL_BAN ||
              v.policy === AUDIENCE_COMPLIANCE_STATUS.PARTIAL_BAN
          );
          if (bannedOnly.length === 0) {
            dispatch(
              addNotification({
                state: LoadingStates.ERROR,
                message: COMPLIANCE_WARNING_BLOCKED_NO_BAN_ROWS,
              })
            );
            return;
          }
          setComplianceWarningTableData(
            bannedOnly.map(
              (v: {
                category: string;
                policy?: string;
                matchedKeywords?: string[];
                matchedBrands?: string[];
              }) => ({
                category: v.category,
                policy_label:
                  v.policy === AUDIENCE_COMPLIANCE_STATUS.FULL_BAN
                    ? COMPLIANCE_WARNING_POLICY_FULL_BAN
                    : COMPLIANCE_WARNING_POLICY_PARTIAL_BAN,
                matched_keywords: [...(v.matchedKeywords ?? [])],
                matched_brands: [...(v.matchedBrands ?? [])],
              })
            )
          );
          setComplianceWarningOpen(true);
          return;
        }

        clearData(false);

        if (resData?.errors) {
          const errors = resData.errors;
          const errorMessages = Object.values(errors).flat();

          if (errorMessages.length > 0) {
            const mergedMessages = errorMessages.join(' ');
            dispatch(
              addNotification({
                state: 'error',
                message: mergedMessages,
              })
            );
          } else {
            dispatch(
              addNotification({
                state: 'error',
                message: AUDIENCE_EXPORT_FAILED_MESSAGE,
              })
            );
          }
        } else {
          dispatch(
            addNotification({
              state: 'error',
              message: AUDIENCE_EXPORT_FAILED_MESSAGE,
            })
          );
        }
      });
  };

  /* Util functions | End */

  const {
    isValid: isValidSegmentName,
    validationMessage: inValidSegmentNameErrorMessage,
  } = isSegmentNameValid(
    [...selectedExportTypes, ...selectedSeedAudienceExportTypes],
    segmentName
  );

  const { isValid: isCPMValidResult, cpmErrorMessage } = isCPMValid(
    [...selectedExportTypes, ...selectedSeedAudienceExportTypes],
    cpm
  );

  const isRequiredFieldMissing = useMemo(() => {
    if (!selectedExportTypes.length && !selectedSeedAudienceExportTypes.length)
      return true;
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    return allExports.some((exportType) => {
      const currentFormData = exportDynamicValues.find(
        (x) => x.exportType === exportType
      );
      if (!currentFormData) return false;
      const {
        partnerID,
        advertiserID,
        tvTargeting,
        platformDestination,
        advertiserDestination,
        metaAdAccountId,
        metaLalRatio,
      } = currentFormData;
      switch (exportType) {
        case AUDIENCE_EXPORT_TYPES.OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.percentageOfMedia
          );
        case AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.percentageOfMedia ||
            (!partnerID && !advertiserID)
          );
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !tvTargeting
          );
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !tvTargeting ||
            (platformDestination && !advertiserDestination) ||
            (!platformDestination && advertiserDestination)
          );
        case AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.percentageOfMedia ||
            !exportDefaultFields.description?.trim()
          );
        case AUDIENCE_EXPORT_TYPES.X_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.X_CUSTOM_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.META_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.MAGNITE_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.MAGNITE_CUSTOM_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.description?.trim() ||
            !metaAdAccountId ||
            !metaLalRatio
          );
        case AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.description?.trim()
          );
        case AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.percentageOfMedia ||
            !exportDefaultFields.description?.trim()
          );
        case AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT:
          return !exportDefaultFields.segmentName;
        case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const advertiserID = exportDynamicValues.find(
            (field: any) => field.id === 'advertiserID'
          )?.value;
          const mobileOs = exportDynamicValues.find(
            (field: any) => field.id === 'mobileOs'
          )?.value;
          const audienceModelingBreadth = exportDynamicValues.find(
            (field: any) => field.id === 'audienceModelingBreadth'
          )?.value;
          const placements = exportDynamicValues.find(
            (field: any) => field.id === 'placements'
          )?.value;
          return (
            !exportDefaultFields.segmentName?.trim() ||
            !advertiserID?.trim() ||
            mobileOs === undefined ||
            mobileOs === '' ||
            !audienceModelingBreadth?.trim() ||
            !Array.isArray(placements) ||
            placements.length === 0
          );
        }
        default:
          return false;
      }
    });
  }, [
    exportDynamicValues,
    exportDefaultFields,
    selectedExportTypes,
    selectedSeedAudienceExportTypes,
  ]);

  const currentAudience = useAppSelector((state) => {
    return state.audienceReducer.currentAudience;
  });

  return (
    <>
      <Grid container spacing={2} style={{ padding: '1rem 1rem 0 1rem' }}>
        <Grid item xs={6}>
          <Box className={classes.audienceDefinitionBox}>
            <span className={classes.header} style={{ marginBottom: 8 }}>
              Combined Audiences
            </span>
            {currentAudience?.audiences?.map((item, index) => (
              <Box key={index}>
                <Typography variant='body1' style={{ paddingBottom: '4px' }}>
                  {modeledAudiences?.find((a: any) => a.id === item.id)?.name ??
                    '-'}
                </Typography>
                {index < currentAudience?.audiences?.length - 1 && (
                  <Divider sx={{ mb: 1 }} />
                )}
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid item xs={6} style={{ paddingRight: 0 }}>
          <Box className={classes.audienceDefinitionBox}>
            <span className={classes.header}>
              Audience Definition{' '}
              <Tooltip title='Audience selection indicator'>
                <span>
                  <Info size={18} weight='fill' />
                </span>
              </Tooltip>
            </span>
            <RenderEstimatedAudienceSize
              lalAudienceCount={currentAudience?.lalAudienceCount}
            />
          </Box>
        </Grid>
      </Grid>

      <div>
        <Box
          sx={{
            flex: 3,
            border: '1px solid #ccc',
            borderRadius: 2,
            margin: '1rem 1rem 2rem 1rem',
            background: 'white',
          }}
        >
          <Typography variant='h2' className={classes.exportTitle}>
            {ACTIVE_AUDIENCES}
          </Typography>
          <DialogContent style={{ overflow: 'hidden' }}>
            <>
              <AudienceExport
                formData={exportDynamicValues}
                exportDynamicFields={exportDynamicFields}
                selectedExportType={selectedExportTypes}
                handleSelectedExportType={handleSelectedExportType}
                selectedSeedAudienceExportTypes={
                  selectedSeedAudienceExportTypes
                }
                segmentName={segmentName}
                isSegmentNameValid={isValidSegmentName}
                inValidSegmentNameErrorMessage={inValidSegmentNameErrorMessage}
                description={description}
                cpm={cpm}
                isCPMValid={isCPMValidResult}
                cpmErrorMessage={cpmErrorMessage}
                percentageOfMedia={percentageOfMedia}
                onChange={handleChangeField}
                isSelectAudienceVisible={isSelectAudienceVisible}
                audienceType={ENUM_SYSTEM_USED.COMBINED_AUDIENCE}
              />
              <div className={classes.exportExportButton}>
                <Tooltip
                  title={
                    isRequiredFieldMissing ? REQUIRED_FIELDS_ARE_MISSING : ''
                  }
                  placement='top'
                  arrow
                >
                  <span>
                    <Button
                      color='primary'
                      disableElevation
                      onClick={() => startExport()}
                      variant='contained'
                      style={{ marginBottom: 15 }}
                      disabled={
                        !selectedExportTypes ||
                        isExporting ||
                        (selectedExportTypes.includes(
                          AUDIENCE_EXPORT_TYPES.KLOVER_IDS
                        ) &&
                          !emails) ||
                        (selectedExportTypes.includes(
                          AUDIENCE_EXPORT_TYPES.RAMP_ID
                        ) &&
                          !emails) ||
                        isRequiredFieldMissing ||
                        !isValidSegmentName ||
                        !isCPMValidResult
                      }
                    >
                      {isExporting ? <CircularProgress size={20} /> : 'Export'}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </>
          </DialogContent>
        </Box>
        <Box
          sx={{
            flex: 3,
            border: '1px solid #ccc',
            borderRadius: 2,
            margin: '0rem 1rem 6rem 1rem',
            background: 'white',
          }}
        >
          <InlineExportHistory audience={audience} />
        </Box>
      </div>
      <ComplianceWarningModal
        open={complianceWarningOpen}
        onClose={handleComplianceWarningClose}
        onCancel={handleComplianceWarningCancel}
        onConfirm={handleComplianceWarningConfirm}
        data={complianceWarningTableData}
      />
    </>
  );
};

export default InlineAudienceExport;
